import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Badge, Disclosure } from '@/components/ui';
import { formatConfidence } from '@/logic/statistics';
import type { DetectionMethod } from '@/store/appStore';
import type {
  BlunderDetectionResults,
  DataSnoopingResults,
  PointPair,
} from '@/types';
import { AllLevelsView, SingleLevelView } from './CombinatorialViews';
import { DataSnoopingView } from './DataSnoopingView';
import {
  DetectionSettings,
  type DetectionSettingsProps,
} from './DetectionSettings';
import { formatCount, pairLabelOf } from './adjustmentDisplay';

export interface DetectionReviewProps extends DetectionSettingsProps {
  blunderResults: BlunderDetectionResults | null;
  dataSnoopingResults: DataSnoopingResults | null;
  combinatorialStale: boolean;
  dataSnoopingStale: boolean;
  /** Method last run on its own; its details open when new results arrive. */
  activeDetectionMethod: DetectionMethod;
  onPreviewRejections: (ids: number[]) => void;
}

const Meta = ({ text, stale }: { text: ReactNode; stale: boolean }) => (
  <span className="inline-flex items-center gap-2">
    <span className="hidden sm:inline">{text}</span>
    {stale && <Badge tone="warning">Outdated</Badge>}
  </span>
);

const combinatorialMeta = (results: BlunderDetectionResults): string => {
  if (results.error) return 'Could not run';
  if (results.isAllLevels)
    return `Levels 1–${results.maxLevel ?? 1} · ${formatCount(results.candidateCount ?? results.activePointCount ?? 0)} candidates`;
  return `${results.rejectionLevel ?? 1}-pair · ${formatCount(results.totalCombinations ?? 0)} trials`;
};

const snoopingMeta = (
  results: DataSnoopingResults,
  pointPairs: PointPair[]
): string => {
  if (results.error) return 'Could not run';
  const outcome = results.finalRejectedIds.length
    ? `rejects ${results.finalRejectedIds.map((id) => pairLabelOf(pointPairs, id)).join(', ')}`
    : 'all pass';
  return `${formatConfidence(results.alpha)} · ${outcome}`;
};

/**
 * Method details below the point table: each method's evidence, with
 * individual runs and tuning in a collapsed section. The cross-method
 * verdict sits beside the solution summary.
 */
export const DetectionReview = ({
  blunderResults,
  dataSnoopingResults,
  combinatorialStale,
  dataSnoopingStale,
  activeDetectionMethod,
  onPreviewRejections,
  ...settings
}: DetectionReviewProps) => {
  const { pointPairs, busy } = settings;
  const [combinatorialOpen, setCombinatorialOpen] = useState(false);
  const [snoopingOpen, setSnoopingOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const comboStale = combinatorialStale && !!blunderResults;
  const snoopStale = dataSnoopingStale && !!dataSnoopingResults;
  const anyStale = comboStale || snoopStale;
  const previewBlockedReason = anyStale
    ? 'Outdated — re-run the check first'
    : busy
      ? 'A calculation is running'
      : undefined;

  // A method run on its own opens its details when the results arrive.
  useEffect(() => {
    if (blunderResults && activeDetectionMethod === 'combinatorial')
      setCombinatorialOpen(true);
  }, [blunderResults, activeDetectionMethod]);
  useEffect(() => {
    if (dataSnoopingResults && activeDetectionMethod === 'dataSnooping')
      setSnoopingOpen(true);
  }, [dataSnoopingResults, activeDetectionMethod]);

  const nothingRun = !blunderResults && !dataSnoopingResults;

  return (
    <section aria-label="Detection review" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="text-base font-semibold text-fg">Detection review</h2>
        <p className="text-xs text-fg-muted">
          {nothingRun
            ? 'Method details appear here after a check.'
            : 'Evidence from each method; the verdict compares them.'}
        </p>
      </div>
      {anyStale && (
        <Alert tone="warning" title="These details describe previous inputs">
          Recommendations are disabled until you re-run the check.
        </Alert>
      )}
      {blunderResults && (
        <Disclosure
          id="combinatorial-details"
          title="Combinatorial search"
          headingLevel={3}
          meta={
            <Meta text={combinatorialMeta(blunderResults)} stale={comboStale} />
          }
          open={combinatorialOpen}
          onOpenChange={setCombinatorialOpen}
        >
          {blunderResults.error ? (
            <Alert tone="danger" title="The search could not run">
              {blunderResults.error}
            </Alert>
          ) : blunderResults.isAllLevels ? (
            <AllLevelsView
              blunderResults={blunderResults}
              pointPairs={pointPairs}
              previewBlockedReason={previewBlockedReason}
              onPreview={onPreviewRejections}
            />
          ) : (
            <SingleLevelView
              blunderResults={blunderResults}
              pointPairs={pointPairs}
              previewBlockedReason={previewBlockedReason}
              onPreview={onPreviewRejections}
            />
          )}
        </Disclosure>
      )}
      {dataSnoopingResults && (
        <Disclosure
          id="data-snooping-details"
          title="Data snooping (τ test)"
          headingLevel={3}
          meta={
            <Meta
              text={snoopingMeta(dataSnoopingResults, pointPairs)}
              stale={snoopStale}
            />
          }
          open={snoopingOpen}
          onOpenChange={setSnoopingOpen}
        >
          {dataSnoopingResults.error ? (
            <Alert
              tone={
                dataSnoopingResults.insufficientRedundancy
                  ? 'warning'
                  : 'danger'
              }
              title="Data snooping could not run"
            >
              {dataSnoopingResults.error}
            </Alert>
          ) : (
            <DataSnoopingView
              results={dataSnoopingResults}
              pointPairs={pointPairs}
              previewBlockedReason={previewBlockedReason}
              onPreview={onPreviewRejections}
            />
          )}
        </Disclosure>
      )}
      <Disclosure
        id="detection-settings"
        title="Run methods individually"
        meta={
          <span className="hidden sm:inline">Level, listing, confidence</span>
        }
        headingLevel={3}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      >
        <DetectionSettings {...settings} />
      </Disclosure>
    </section>
  );
};
